import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Briefcase,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Phone,
  Search,
  Trash2,
  User,
  Users,
  X,
} from "lucide-react";
import "../../CSS/Global.css";
import "../Admission/CustomerStyle.css";
import EmptyStateBlock from "../EmptyStateBlock";
import { useSearch } from "../../SearchContext";
import { useFilter } from "../../FilterContext";
import Loader from "../Loader";
import Swal from "sweetalert2";
import { decryptToken } from "../utils/getToken";
import { getSessionMainId } from "../utils/getCookie";

function formatDepartment(raw) {
  if (raw == null || raw === "") return null;
  try {
    const dep = JSON.parse(raw);
    if (Array.isArray(dep)) return dep.join(", ");
    return String(dep);
  } catch {
    return String(raw);
  }
}

function staffDisplayName(e) {
  const n = `${e?.firstName || ""} ${e?.lastName || ""}`.trim();
  return n || "Unnamed";
}

function profileSlug(e) {
  const n = staffDisplayName(e);
  return encodeURIComponent(n.replace(/\s+/g, "-").slice(0, 80) || "profile");
}

const TeamDirectory = () => {
  const { globalSearch } = useSearch();
  const { entriesPerPage } = useFilter();
  const [currentPage, setCurrentPage] = useState(1);
  const [load, setLoad] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [teamSearch, setTeamSearch] = useState("");
  const navigate = useNavigate();
  const token = decryptToken("accessToken") || "";
  const apiUrl = API_BASE_URL;

  const authHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const canBulkDelete = useCallback(() => {
    if (!token) return false;
    try {
      const d = jwtDecode(token);
      const r = String(d.role ?? d.type ?? "").toLowerCase();
      return r === "admin" || r === "superadmin";
    } catch {
      return false;
    }
  }, [token]);

  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    if (!bulkSelectMode) setSelectedIds([]);
  }, [bulkSelectMode]);

  const toggleSelectId = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelectedIds([]);

  const safeDate = (date) => (date ? new Date(date).toLocaleDateString() : null);

  const fetchEmployees = async () => {
    setLoad(true);
    try {
      const mainId = getSessionMainId();
      const staffQs = mainId ? `?main_id=${encodeURIComponent(mainId)}` : "";
      const res = await fetch(`${apiUrl}/staff${staffQs}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch employees");
      const data = await res.json();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load team directory.");
      setEmployees([]);
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const q = (teamSearch.trim() || globalSearch.trim()).toLowerCase();
  const filteredEmployees = employees.filter((employee) => {
    if (!q) return true;
    const dep = formatDepartment(employee.department) || "";
    const hay = [
      staffDisplayName(employee),
      employee.emplyee_ID,
      employee.phone,
      employee.job_title,
      employee.gender,
      dep,
      employee.email,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [q, entriesPerPage]);

  const totalMatching = filteredEmployees.length;
  const totalPages = Math.max(1, Math.ceil(totalMatching / entriesPerPage) || 1);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const pageLabelFrom = totalMatching === 0 ? 0 : indexOfFirst + 1;
  const pageLabelTo = Math.min(indexOfLast, totalMatching);
  const searchActive = Boolean(teamSearch.trim() || globalSearch.trim());

  const selectAllOnCurrentPage = () => {
    const pageIds = currentEmployees
      .map((e) => e.id)
      .filter((x) => x != null)
      .map(Number)
      .filter(Number.isFinite);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const handleBulkDelete = async () => {
    if (!canBulkDelete() || selectedIds.length === 0) return;
    const first = await Swal.fire({
      title: `Delete ${selectedIds.length} team member(s)?`,
      html: `<p style="text-align:left;margin:0 0 8px">This permanently removes selected staff records from your directory.</p><p style="text-align:left;margin:0;font-size:13px;color:#64748b">You cannot undo this action.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#b91c1c",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Delete",
      focusCancel: true,
    });
    if (!first.isConfirmed) return;

    setBulkDeleting(true);
    try {
      const res = await fetch(`${apiUrl}/staff/bulk-delete`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.isError) {
        throw new Error(data.message || "Bulk delete failed");
      }
      const removed = new Set(selectedIds);
      setEmployees((prev) => (Array.isArray(prev) ? prev.filter((e) => !removed.has(Number(e.id))) : []));
      clearSelection();
      setBulkSelectMode(false);
      toast.success(data.message || `Removed ${data.deleted ?? selectedIds.length} record(s).`);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e.message || "Bulk delete failed", "error");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDelete = async (employeeId) => {
    Swal.fire({
      title: "Remove this team member?",
      text: "They will be removed from the directory.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#b91c1c",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await fetch(`${apiUrl}/staff/${employeeId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (!res.ok || data.isError) {
          throw new Error(data.message || "Failed to delete");
        }
        setEmployees((prev) => prev.filter((item) => item.id !== employeeId));
        setSelectedIds((prev) => prev.filter((id) => id !== Number(employeeId)));
        toast.success("Team member removed.");
      } catch (error) {
        console.error(error);
        toast.error(error.message || "Failed to delete.");
      }
    });
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const openProfile = (employee) => {
    navigate(`/employee-profile/${employee.id}/${profileSlug(employee)}`);
  };

  if (load) {
    return <Loader />;
  }

  return (
    <>
      <div
        className={`admission-page${
          bulkSelectMode && canBulkDelete() && selectedIds.length > 0 ? " admission-page--bulk-dock" : ""
        }`}
      >
        <div className="admission-controls">
          <div className="customers-header admission-page__toolbar admission-page__toolbar--integrated">
            <div className="customer-dropdown admission-status-filter-anchor">
              <p className="dashboard-title">Team directory</p>
            </div>

            <div className="customer-warning-new-hamburger-help">
              <div className="customer-new admission-toolbar-actions">
                {canBulkDelete() && (
                  <button
                    type="button"
                    className={`admission-bulk-mode-btn${bulkSelectMode ? " admission-bulk-mode-btn--active" : ""}`}
                    onClick={() => setBulkSelectMode((m) => !m)}
                    aria-pressed={bulkSelectMode}
                  >
                    {bulkSelectMode ? "Cancel select" : "Select"}
                  </button>
                )}
                <Link to="/new-staff">
                  <button type="button" className="oms-add-btn">
                    <i className="fa-solid fa-plus" aria-hidden /> New
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {bulkSelectMode && canBulkDelete() && (
            <div className="admission-bulk-strip admission-bulk-strip--embedded" role="region" aria-label="Bulk selection">
              <div className="admission-bulk-strip__inner">
                <span className="admission-bulk-strip__count">
                  <strong>{selectedIds.length}</strong> selected
                  {totalMatching > 0 && (
                    <span className="admission-bulk-strip__hint"> · across pages when you navigate</span>
                  )}
                </span>
                <div className="admission-bulk-strip__btns">
                  <button type="button" className="admission-bulk-strip__linkish" onClick={selectAllOnCurrentPage}>
                    All on this page
                  </button>
                  <button
                    type="button"
                    className="admission-bulk-strip__linkish"
                    onClick={clearSelection}
                    disabled={selectedIds.length === 0}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="admission-controls__search">
            <div className="admission-search-summary__search">
              <Search className="admission-search-summary__icon" size={17} strokeWidth={2.25} aria-hidden />
              <input
                type="search"
                className="admission-search-summary__input"
                placeholder="Search name, employee ID, phone, job title, department…"
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                aria-label="Search team"
              />
              {teamSearch.trim() !== "" && (
                <button
                  type="button"
                  className="admission-search-summary__clear admission-search-summary__clear--icon"
                  onClick={() => setTeamSearch("")}
                  aria-label="Clear search"
                >
                  <X size={18} strokeWidth={2.25} aria-hidden />
                </button>
              )}
            </div>
            <div className="admission-search-summary__stats" role="status">
              <span className="admission-search-summary__pill" title="Listed after search">
                <strong>{employees.length}</strong> team member{employees.length === 1 ? "" : "s"}
              </span>
              {searchActive && (
                <span className="admission-search-summary__pill admission-search-summary__pill--accent">
                  <strong>{totalMatching}</strong> match{totalMatching === 1 ? "" : "es"}
                </span>
              )}
              {totalMatching > 0 && (
                <span className="admission-search-summary__range">
                  Rows {pageLabelFrom}–{pageLabelTo} of {totalMatching}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="admission-cards-grid">
          {totalMatching === 0 && employees.length > 0 ? (
            <EmptyStateBlock
              Icon={Users}
              title="No team members match your search"
              description="Try different keywords or clear the search box."
              className="admission-empty--wide admission-empty-center max-w-full"
            />
          ) : currentEmployees.length > 0 ? (
            currentEmployees.map((employee, index) => (
              <div
                className={`ecom-customer-card${bulkSelectMode && canBulkDelete() ? " ecom-customer-card--selectable" : ""}`}
                key={employee.id ?? index}
                onClick={() => {
                  if (bulkSelectMode && canBulkDelete()) return;
                  openProfile(employee);
                }}
                style={{ cursor: bulkSelectMode && canBulkDelete() ? "default" : "pointer" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (bulkSelectMode && canBulkDelete()) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openProfile(employee);
                  }
                }}
              >
                {bulkSelectMode && canBulkDelete() && (
                  <label
                    className="admission-card-checkbox-wrap"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="admission-card-checkbox-input"
                      checked={selectedIds.includes(Number(employee.id))}
                      onChange={() => toggleSelectId(employee.id)}
                      aria-label={`Select ${staffDisplayName(employee)}`}
                    />
                    <span className="admission-card-checkbox-face" aria-hidden />
                  </label>
                )}
                <div className="admission-card__hero">
                  <div className="admission-card__hero-top">
                    <div className="admission-card__avatar admission-card__avatar--placeholder" aria-hidden>
                      <User size={26} strokeWidth={2} />
                    </div>
                    <div className="admission-card__intro">
                      <h2 className="ecom-customer-name admission-card__name">{staffDisplayName(employee)}</h2>
                      {employee.emplyee_ID && (
                        <div className="admission-card__sub" title="Employee ID">
                          {employee.emplyee_ID}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="admission-card__control-row">
                    {safeDate(employee.dob) ? (
                      <div className="ecom-customer-time admission-card__time" title="Date of birth">
                        <Calendar size={13} aria-hidden strokeWidth={2.25} />
                        <span>{safeDate(employee.dob)}</span>
                      </div>
                    ) : (
                      <span className="admission-card__control-spacer" aria-hidden />
                    )}
                    <div
                      className="ecom-customer-actions admission-card__actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="ecom-edit-btn"
                        type="button"
                        title="Edit"
                        aria-label="Edit team member"
                        onClick={() => navigate("/new-staff", { state: { ...employee } })}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="ecom-delete-btn"
                        type="button"
                        title="Delete"
                        aria-label="Delete team member"
                        onClick={() => handleDelete(employee.id)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="admission-card__body">
                  {employee.phone && (
                    <div className="ecom-customer-row admission-card__row admission-card__row--contact">
                      <Phone className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                      <span>{employee.phone}</span>
                    </div>
                  )}
                  {employee.job_title && (
                    <div className="ecom-customer-row admission-card__row">
                      <Briefcase className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                      <span>{employee.job_title}</span>
                    </div>
                  )}
                  {formatDepartment(employee.department) && (
                    <div className="ecom-customer-row admission-card__row">
                      <Building2 className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                      <span>{formatDepartment(employee.department)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <EmptyStateBlock
              Icon={Users}
              title="No team members yet"
              description="Add employees from New staff to build your directory."
              className="admission-empty--wide admission-empty-center max-w-full"
            />
          )}
        </div>

        {bulkSelectMode && canBulkDelete() && selectedIds.length > 0 && (
          <div className="admission-bulk-dock" role="toolbar" aria-label="Bulk delete">
            <div className="admission-bulk-dock__panel">
              <div className="admission-bulk-dock__text">
                <span className="admission-bulk-dock__n">{selectedIds.length}</span>
                <span>team member{selectedIds.length === 1 ? "" : "s"} marked</span>
              </div>
              <div className="admission-bulk-dock__actions">
                <button type="button" className="admission-bulk-dock__secondary" onClick={clearSelection} disabled={bulkDeleting}>
                  Clear
                </button>
                <button type="button" className="admission-bulk-dock__danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
                  <Trash2 size={17} strokeWidth={2.25} aria-hidden />
                  {bulkDeleting ? "Deleting…" : "Delete selected"}
                </button>
              </div>
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination admission-pagination">
            <button
              type="button"
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronLeft size={18} />
            </button>
            <button type="button" className="pagination-btn active">
              {currentPage}
            </button>
            <button
              type="button"
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronRight size={18} />
            </button>
            <button
              type="button"
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default TeamDirectory;
